import React from "react";
import "@/App.css";
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
    useLocation,
} from "react-router-dom";
import { AppProvider, useApp } from "@/lib/store";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import AIManager from "@/pages/AIManager";
import Reputation from "@/pages/Reputation";
import Approvals from "@/pages/Approvals";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Onboarding from "@/pages/Onboarding";
import Audit from "@/pages/Audit";
import AdminConsole from "@/pages/AdminConsole";
import CompanyProfile from "@/pages/CompanyProfile";
import Inbox from "@/pages/Inbox";
import SEO from "@/pages/SEO";
import Presence from "@/pages/Presence";
import Landing from "@/marketing/Landing";
import { Privacy, Terms, DataDeletion } from "@/marketing/Legal";
import { Loader2 } from "lucide-react";

function ProtectedShell() {
    const { user, businesses, bootstrapping } = useApp();
    if (bootstrapping)
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#090d16]">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
        );
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "super_admin") return <Navigate to="/admin" replace />;
    if (businesses.length === 0) return <Navigate to="/onboarding" replace />;
    return <Layout />;
}

// "/" is the public website for visitors and the dashboard for signed-in users.
function HomeGate() {
    const { user, bootstrapping } = useApp();
    if (bootstrapping)
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#090d16]">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
        );
    if (!user) return <Landing />;
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
}

function AuthedOnly({ children }) {
    const { user, bootstrapping } = useApp();
    if (bootstrapping)
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#090d16]">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
        );
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function SuperAdminOnly({ children }) {
    const { user, bootstrapping } = useApp();
    if (bootstrapping)
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#090d16]">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
            </div>
        );
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "super_admin") return <Navigate to="/" replace />;
    return children;
}

function Router() {
    const loc = useLocation();
    if (loc.hash?.includes("session_id=")) return <AuthCallback />;

    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Navigate to="/register" replace />} />
            <Route
                path="/onboarding"
                element={
                    <AuthedOnly>
                        <Onboarding />
                    </AuthedOnly>
                }
            />
            <Route
                path="/admin"
                element={
                    <SuperAdminOnly>
                        <AdminConsole />
                    </SuperAdminOnly>
                }
            />
            <Route path="/" element={<HomeGate />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route element={<ProtectedShell />}>
                <Route path="dashboard" element={<Home />} />
                <Route path="presence" element={<Presence />} />
                <Route path="ai-manager" element={<AIManager />} />
                <Route path="audit" element={<Audit />} />
                <Route path="reputation" element={<Reputation />} />
                <Route path="approvals" element={<Approvals />} />
                <Route path="reports" element={<Reports />} />
                <Route path="settings" element={<Settings />} />
                <Route path="company-profile" element={<CompanyProfile />} />
                <Route path="inbox" element={<Inbox />} />
                <Route path="seo" element={<SEO />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AppProvider>
                <Router />
            </AppProvider>
        </BrowserRouter>
    );
}
